// A PostMessage fire-and-forget channel implementation that deals with setting
// up a connection and buffering messages until someone is listening on the
// other side.
//
//  Usage (outer page):
//  const chan = channel.Outer<MsgType>(iframe);
//  chan.listen((msg) => { ... });
//  chan.send(<someMessage>);
//
//  Usage (inside iframe):
//  const chan = channel.Inner<MsgType>();
//  chan.listen((msg) => { ... });
//  chan.send(<someMessage>);
//
// We guarantee that messages sent will be buffered and sent later when the other side connects. So no need to dance around or do a handshake.
// Also, messages come out as typed JSON payloads.
//
// TODO(jaime or joel): Port the recording script to use this. Should clean things up a bit.
enum MsgType {
  Connect = 1,
  Hello,
  Ack,
  Data,
}

// Obtain a channel if you are running inside the IFrame.
export function Inner<T>(): FireAndForgetChannel<T> {
  return new FireAndForgetChannel<T>(window.parent, window);
}

// Obtain a channel if you are running in the outer page and want to talk to an IFrame.
export function Outer<T>(frameToTalkTo: HTMLIFrameElement): FireAndForgetChannel<T> {
  return new FireAndForgetChannel<T>(frameToTalkTo.contentWindow, window);
}

type Envelope<T> =
  | {
      Type: MsgType.Data;
      Payload: T;
    }
  | {
      Type: MsgType.Connect;
    }
  | {
      Type: MsgType.Ack;
    };

// A bi-directional fire-and-forget channel that uses postmessage. Intended for
// communication between an IFrame and the page containing it.
export class FireAndForgetChannel<T> {
  private _wndToListenTo: Window;
  private _listeners: ((msg: T) => void)[] = [];

  // A buffer for messages sent before the channel is connected. This is set to
  // null once we know that there is a channel connected and listening on the
  // other side.
  private _buffer: T[] | null = [];

  private _msgEvtListener: (e: MessageEvent) => void;

  constructor(
    private _wndToSendTo: Window | null,
    wndToListenTo: Window | null,
    private _origin: string = '*',
  ) {
    // TODO(jaime): Require a channel id for channel construction, to disambiguate calls to multiple instances on a single window.
    this._msgEvtListener = (evt: MessageEvent) => {
      const envelope = evt.data as Envelope<T>;
      switch (envelope.Type) {
        case MsgType.Data:
          this._onMessage(envelope.Payload);
          break;
        case MsgType.Connect: {
          const source = evt.source as WindowProxy | null;
          if (!source) {
            break;
          }
          source.postMessage({ Type: MsgType.Ack }, _origin);
          this._drain();
          break;
        }
        case MsgType.Ack:
          this._drain();
      }
    };

    if (!wndToListenTo) {
      throw new Error(`No window to listen to`);
    }
    this._wndToListenTo = wndToListenTo;

    // TODO(jaime): Dispose of this native event listener since it is leaking.
    this._wndToListenTo.addEventListener('message', this._msgEvtListener);

    // Eagerly send a connect if we can.
    this.init();
  }

  init(wndToSendTo: Window | null = this._wndToSendTo) {
    if (!wndToSendTo) {
      return;
    }
    this._wndToSendTo = wndToSendTo;
    wndToSendTo.postMessage({ Type: MsgType.Connect }, this._origin);
  }

  dispose() {
    this._wndToListenTo.removeEventListener('message', this._msgEvtListener);
  }

  // Adds a listener to the list. Returns a function that stops listening.
  listen(listener: (msg: T) => void): () => void {
    this._listeners.push(listener);
    // Return a remover.
    return () => {
      for (let i = 0; i < this._listeners.length; i++) {
        if (this._listeners[i] == listener) {
          this._listeners.splice(i, 1);
          return;
        }
      }
    };
  }

  send(msg: T) {
    if (this._buffer) {
      this._buffer.push(msg);
    } else if (!this._wndToSendTo) {
      throw new Error(`Drained message queue without a window to send to?`);
    } else {
      this._send(this._wndToSendTo, msg);
    }
  }

  private _drain() {
    // Be resilient to receiving both connect and ack. If we have already drained, then don't drain.
    if (!this._buffer) {
      return;
    }

    // Be resilient to receiving a Connect before we have initialized fires.
    if (!this._wndToSendTo) {
      return;
    }

    for (const bufferedMessage of this._buffer) {
      this._send(this._wndToSendTo, bufferedMessage);
    }

    // Done with these.
    this._buffer = null;
  }

  private _send(wndToSendTo: Window, msg: T) {
    const envelope: Envelope<T> = {
      Type: MsgType.Data,
      Payload: msg,
    };
    wndToSendTo.postMessage(envelope, this._origin);
  }

  private _onMessage(msg: T) {
    this._listeners.forEach(listener => {
      listener(msg);
    });
  }
}

export function Lazy<T>(frameToTalkTo: HTMLIFrameElement): FireAndForgetChannel<T> {
  return new LazyFireAndForgetChannel<T>(frameToTalkTo);
}

export class LazyFireAndForgetChannel<T> extends FireAndForgetChannel<T> {
  constructor(iframe: HTMLIFrameElement) {
    super(iframe.contentWindow, window);
    iframe.addEventListener('load', () => {
      this.init(iframe.contentWindow);
    });
  }
}
